/**
 * Document Verification Landing Page
 *
 * Allows users to manually enter a verification code
 * Redirects to /verify/:code for actual verification
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  InputAdornment,
} from '@mui/material'
import { QrCodeScanner, Search, VerifiedUser } from '@mui/icons-material'
import { Button, PageHeader } from '@components/common'

const VerifyLanding = () => {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    const trimmedCode = code.trim().toUpperCase()

    if (!trimmedCode) {
      setError('Please enter a verification code')
      return
    }

    if (trimmedCode.length < 20) {
      setError('Verification code appears to be too short')
      return
    }

    setError('')
    navigate(`/verify/${trimmedCode}`)
  }

  return (
    <Box>
      <PageHeader
        title="Verify Document"
        subtitle="Enter a verification code to verify document authenticity"
      />

      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <VerifiedUser sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Document Verification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the verification code found on your printed document to verify its authenticity.
              The code is typically located near the QR code at the bottom of the document.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Code Entry Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Verification Code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase())
                setError('')
              }}
              placeholder="e.g., 7A3F9B2C1D4E5F6A8B9C0D1E"
              error={!!error}
              helperText={error || 'Enter the 24-character code from the document'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScanner color="action" />
                  </InputAdornment>
                ),
                sx: {
                  fontFamily: 'monospace',
                  letterSpacing: 2,
                  fontSize: '1.1rem',
                }
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              size="large"
              startIcon={<Search />}
            >
              Verify Document
            </Button>
          </form>

          <Divider sx={{ my: 3 }} />

          {/* Help Section */}
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              How to verify a document:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Find the QR code on your printed document (usually at the bottom)</li>
                <li>Scan the QR code with your phone camera, OR</li>
                <li>Enter the verification code shown below the QR code above</li>
                <li>The system will verify if the document is authentic and unmodified</li>
              </ol>
            </Typography>
          </Box>

          {/* Status Legend */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Verification Results:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2">
                  <strong>Verified</strong> - Document is authentic and unchanged
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="body2">
                  <strong>Modified</strong> - Document has been altered since printing
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main' }} />
                <Typography variant="body2">
                  <strong>Revoked/Not Found</strong> - Document is invalid
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default VerifyLanding
